/* eslint-disable react/prop-types */
import React, { useContext, useEffect } from 'react';
import { useStore } from '@nanostores/react';
import uniqid from 'uniqid';
import { StoreContext } from '../../../store';
import Wrapper from './Wrapper';

const sortElements = (elements, sortMode) => {
  if (!elements) {
    return [];
  }

  if (sortMode === 'Every other') {
    // Collect all types
    // Elements without a type prop get added as 'undefined'
    const types = {};

    elements.flat().forEach((element) => {
      const type = element.props?.internalName;
      if (!types[type]) {
        types[type] = [];
      }

      types[type].push(element);
    });

    // Get the max length of the longest type array
    const maxLength = Math.max(
      ...Object.values(types).map((arr) => arr.length),
    );

    // Sort the elements by type
    const sorted = [];
    for (let i = 0; i < maxLength; i += 1) {
      Object.keys(types).forEach((key) => {
        const typeArray = types[key];
        if (i < typeArray.length) {
          sorted.push(typeArray[i]);
        }
      });
    }

    return sorted;
  }

  return elements.flat();
};

const List = ({
  elements = [],
  moreLabel,
  lessLabel,
  visibleElements: initialVisibleElements,
  topItemId,
  sortMode: initialSortMode,
  stepSize: initialStepSize,
  getFilteredElementsData = (elems) => ({
    filteredElements: elems,
    contentTypeCount: {},
  }),
  fallback,
}) => {
  const visibleElements = initialVisibleElements || 5;
  const sortMode = initialSortMode || 'default';
  const stepSize = initialStepSize || 5;

  const { filteredElements, contentTypeCount } =
    getFilteredElementsData(elements);

  const { initStore } = useContext(StoreContext);
  const store = initStore(topItemId, {
    visibleItems: visibleElements,
    sortMode,
    contentTypesCount: contentTypeCount,
    totalHits: elements.length,
  });

  const { contentTypesCount } = store.get();

  useEffect(() => {
    function getHasContentTypesChanged() {
      const keys1 = Object.keys(contentTypeCount);
      const keys2 = Object.keys(contentTypesCount);

      if (keys1.length !== keys2.length) {
        return true;
      }

      const hasChanged = keys1.some(
        (key) => contentTypesCount[key] !== contentTypeCount[key],
      );

      return hasChanged;
    }
    const contentTypeHasChanged = getHasContentTypesChanged();

    if (contentTypeHasChanged) {
      store.setKey('contentTypesCount', contentTypeCount);
    }
  }, [contentTypeCount, contentTypesCount, store]);

  const { visibleItems } = useStore(store);

  const sortedElements = sortElements(filteredElements, sortMode);
  const elementsToRender = sortedElements.slice(0, visibleItems);

  if (!elementsToRender.length && fallback) {
    return fallback;
  }
  return (
    <Wrapper
      moreLabel={moreLabel}
      lessLabel={lessLabel}
      numOfRenderedElems={elementsToRender?.length}
      numOfTotalElems={elements.length}
      numOfFilteredElems={filteredElements?.length}
      initialNumberOfVisibleElements={visibleElements}
      onCollapse={() => store.setKey('visibleItems', visibleElements)}
      onExpand={() => store.setKey('visibleItems', visibleItems + stepSize)}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
        {elementsToRender.map((element) => (
          <div key={uniqid('list-item-')}>{element}</div>
        ))}
      </div>
    </Wrapper>
  );
};
List.app = 'idun';
List.displayName = 'List';

export default List;
